import dayjs from "dayjs"

import { PE_STATUSES } from "./packagingExecutions"

export const orderedPackagingTypes = ["CAPPED", "REUSABLE", "DISPOSABLE"]

export const getOrderedPEPackagings = (packagings = []) => {
  return packagings.sort((a, b) => orderedPackagingTypes.indexOf(a.type) - orderedPackagingTypes.indexOf(b.type))
}

export const getOrderedPEPackagingsWithNoTypes = (packagings = []) => {
  return orderedPackagingTypes.map(type => packagings.find(p => p.type === type) || {})
}

export const PE_STEPPER_OPTIONS = [
  { value: "BEFORE_START", label: "Avant le début du barquettage" },
  { value: orderedPackagingTypes[0], label: "Operculé" },
  { value: orderedPackagingTypes[1], label: "Réemployable" },
  { value: orderedPackagingTypes[2], label: "Jetable" },
  { value: PE_STATUSES.packagingsCounting, label: "Nbr de barquettes" },
  { value: PE_STATUSES.waste, label: "Pesée des restes" }
]

export const PES_SIDEBAR_MENUS_ENUM = {
  OUTPUT: "output",
  WEIGHT_CONTROL: "weightControl",
  TO_PACK: "toPack",
  TASTING: "tasting"
}

export const PES_SIDEBAR_MENUS_LABELS = {
  [PES_SIDEBAR_MENUS_ENUM.OUTPUT]: "Barquettes à sortir",
  [PES_SIDEBAR_MENUS_ENUM.WEIGHT_CONTROL]: "Contrôler les poids",
  [PES_SIDEBAR_MENUS_ENUM.TO_PACK]: "À barquetter",
  [PES_SIDEBAR_MENUS_ENUM.TASTING]: "À goûter"
}

export const DEFAULT_SELECTED_PES_SIDEBAR_MENU = PES_SIDEBAR_MENUS_ENUM.WEIGHT_CONTROL

/**
 * Get the default real weight for a section
 * If the section has a realWeight, use it, otherwise use the recipeSectionWeight
 * @param {*} section 
 * @returns 
 */
export const getDefaultSectionRealWeight = (section) => {
  let sectionWeight = section.recipeSectionWeight || 0

  if (section.realWeight !== undefined) {
    sectionWeight = section.realWeight
  }

  return sectionWeight
}

export const getSectionProductionWeight = (section) => {
  let sectionProductionWeight = section.initialProductionWeight

  if (Array.isArray(section.counterWeighing) && section.counterWeighing.length > 0) {
    // Use the last counterWeighing's weight
    sectionProductionWeight = section.counterWeighing[section.counterWeighing.length - 1].weight
  } else if (section.counterWeighing?.weight) {
    // Fallback for single object
    sectionProductionWeight = section.counterWeighing.weight
  }

  return sectionProductionWeight
}

/**
 * Distribute packaging numbers for reproduction
 * 1st CAPPED, then REUSABLE, then DISPOSABLE
 * @param {*} packagings 
 * @param {*} initialNumber 
 * @param {*} returnedField 
 * @returns 
 */
const distributePackagingNumbersForReprod = (packagings = [], initialNumber, returnedField) => {
  // 1. Order packagings by type (CAPPED, REUSABLE, DISPOSABLE)
  const orderPackagings = getOrderedPEPackagings(packagings) || []

  const newPackagings = []
  let totalPackagingNumber = 0
  let eachPackagingNumber = initialNumber

  for (const packaging of orderPackagings) {
    // 2. Distribute the initial number of packagings
    const minNumber = Math.min(eachPackagingNumber, packaging.theoreticalNumber)

    newPackagings.push({
      ...packaging,
      [returnedField]: minNumber,
      forecastNumber: packaging.forecastNumber || 0
    })
    eachPackagingNumber -= minNumber
    totalPackagingNumber += minNumber
  }

  return {
    newPackagings,
    totalPackagingNumber
  }
}


/**
 * Compute total incompressible packaging needs based on brand rules
 * @param {*} packagingNeeds - Object with brands and their packaging needs
 * @returns {number} - Total incompressible needs across all brands
 */
export const calculateIncompressiblePackagingTotal = (packagingNeeds) => {
  let incompressibleTotal = 0

  Object.keys(packagingNeeds).forEach(brand => {
    const brandData = packagingNeeds[brand]

    if (brandData.incompressiblePackagingVolumes) {
      // Sum all packaging types for this incompressible brand
      Object.keys(brandData).forEach(key => {
        if (key !== "incompressiblePackagingVolumes") incompressibleTotal += brandData[key] || 0
      })
    }
  })

  return incompressibleTotal
}

/**
 * Calculate reproduction weights (incompressible/compressible) for a section
 */
const calculateSectionReproductionWeights = (section, expectedPackagingNumber, incompressibleTotal) => {
  const hasCounterWeighing = section.counterWeighing &&
    (Array.isArray(section.counterWeighing) ? section.counterWeighing.length > 0 : section.counterWeighing.weight)

  if (!hasCounterWeighing) return { ...section, incompressiblePackagingWeight: 0, compressiblePackagingWeight: 0 }

  const sectionTheoreticalWeight = section.totalTheoreticalWeight || 0
  const sectionProductionWeight = getSectionProductionWeight(section) || 0

  if (sectionProductionWeight >= sectionTheoreticalWeight) return { ...section, incompressiblePackagingWeight: 0, compressiblePackagingWeight: 0 }


  const totalMissingWeight = sectionTheoreticalWeight - sectionProductionWeight
  const sectionRatio = sectionTheoreticalWeight > 0 ? sectionProductionWeight / sectionTheoreticalWeight : 0
  const sectionPackagingsProducible = Math.floor(sectionRatio * expectedPackagingNumber)

  // Section can produce more than total incompressible needs --> all compressible
  if (sectionPackagingsProducible >= incompressibleTotal) return { ...section, incompressiblePackagingWeight: 0, compressiblePackagingWeight: totalMissingWeight }

  // Section needs both incompressible + compressible reproduction
  const sectionShareOfIncompressible = expectedPackagingNumber > 0
    ? (sectionTheoreticalWeight / expectedPackagingNumber) * incompressibleTotal
    : 0

  const incompressibleMissingFromShare = Math.max(0, sectionShareOfIncompressible - sectionProductionWeight)
  const incompressibleToReproduce = Math.min(incompressibleMissingFromShare, totalMissingWeight)
  const compressibleToReproduce = Math.max(0, totalMissingWeight - incompressibleToReproduce)

  return {
    ...section,
    incompressiblePackagingWeight: incompressibleToReproduce,
    compressiblePackagingWeight: compressibleToReproduce
  }
}

/**
 * Form when doing a reprod (after the counter weighing form) 
 * @param {*} packagingExecution The packaging execution to reprod
 * @param {*} inProgressRealizedNumber Input from the previous dialog (realizedNumber)
 * @returns 
 */
export const getPEReprodFormInitialValues = (
  packagingExecution,
  inProgressRealizedNumber = 0,
  planningRules = {}
) => {
  const expectedPackagingNumber = packagingExecution.expectedPackagingNumber || 0
  // realized number is only for PE in progress
  const { newPackagings, totalPackagingNumber: totalRealizedNumber } = distributePackagingNumbersForReprod(
    packagingExecution.packagings,
    inProgressRealizedNumber,
    "realizedNumber"
  )
  const packagingNeeds = getPackagingNeedsByBrand(packagingExecution.productionItems, planningRules)
  const realizableNumbers = []
  const sections = []

  for (const section of packagingExecution.sections) {
    const defaultRealWeight = getDefaultSectionRealWeight(section)
    const packagingForecastNumber = calculatePackagingForecastNumberBySection(section, defaultRealWeight)
    const sectionWeight = getSectionProductionWeight(section) // in kilos

    const realWeight = ((packagingExecution.expectedPackagingNumber - (inProgressRealizedNumber || 0)) * defaultRealWeight) - sectionWeight

    sections.push({
      ...section,
      realWeight: realWeight < 0 ? 0 : realWeight,
      proposedWeight: sectionWeight || 0,
      forecastWaste: section.forecastWaste || 0,
      packagingForecastNumber,
    })

    const realizableNumber = sectionWeight / defaultRealWeight

    realizableNumbers.push(realizableNumber)
  }

  // Compute the global packaging forecast number based on the sections (minimum of all sections)
  const globalPackagingForecastNumber = calculateGlobalPackagingForecastNumber(sections)
  // Compute the packagings forecast number based on the global packaging forecast number for each packaging
  const updatedPackagings = calculatePackagingsForecastNumber(newPackagings, globalPackagingForecastNumber, expectedPackagingNumber, packagingNeeds)

  const minRealizableNumber = Math.min(...realizableNumbers)
  const {
    newPackagings: updatedNewPackagings,
  } = distributePackagingNumbersForReprod(updatedPackagings, minRealizableNumber, "realizableNumber")

  // Compute total incompressible packaging needs
  const incompressibleTotal = calculateIncompressiblePackagingTotal(packagingNeeds)

  // Calculate reproduction weights for each section
  const newSections = sections.map((section) =>
    calculateSectionReproductionWeights(section, expectedPackagingNumber, incompressibleTotal)
  )

  return {
    ...packagingExecution,
    expectedPackagingNumber,
    packagings: updatedNewPackagings,
    sections: newSections,
    totalRealizedNumber, // only for PE in progress
    totalRealizableNumber: minRealizableNumber,
    // won't be saved in db for display only and to ease waste calculations
    packagingForecastNumber: globalPackagingForecastNumber,
  }
}

export const getReprodSectionNetWeight = (section) => Number(section.incompressiblePackagingWeight || 0) + Number(section.compressiblePackagingWeight || 0)

export const calculatePackagingForecastNumberBySection = (section, realWeight = 1) => {
  const realWeightKilos = Number(realWeight)
  const sectionProductionWeight = getSectionProductionWeight(section) // in kilos
  if (!sectionProductionWeight) return 0

  let packagingForecastNumber = Math.floor(+(sectionProductionWeight / realWeightKilos).toFixed(0))

  if (!isFinite(packagingForecastNumber)) { // if realWeight is 0 the division will return Infinity
    packagingForecastNumber = 0
  }

  return packagingForecastNumber
}

/**
 * Computes the global packaging forecast number based on the sections.
 * It finds the minimum packaging forecast number from the sections that have a valid packaging forecast number.
 * @param {*} sections 
 * @returns 
 */
export const calculateGlobalPackagingForecastNumber = (sections = []) => {
  const sectionsWithPackagingForecastNumber = sections.filter(section => section.packagingForecastNumber && isFinite(section.packagingForecastNumber))
  const packagingForecastNumbers = sectionsWithPackagingForecastNumber.map(section => section.packagingForecastNumber)
  if (packagingForecastNumbers.length <= 0) return 0
  const minimumPackagingForecastNumber = Math.min(...packagingForecastNumbers)

  return minimumPackagingForecastNumber
}

/**
 * Computes the forecast number for each packaging based on the global forecast number.
 * Prioritizes incompressible packaging types first, then distributes remaining capacity proportionally.
 * @param {*} packagings 
 * @param {*} globalPackagingForecastNumber 
 * @param {*} expectedPackagingNumber 
 * @param {*} packagingNeeds - Brand packaging needs with incompressibility flags
 * @returns 
 */
export const calculatePackagingsForecastNumber = (packagings = [], globalPackagingForecastNumber = 0, expectedPackagingNumber = 0, packagingNeeds = {}) => {
  if (!expectedPackagingNumber || expectedPackagingNumber === 0) return packagings.map(p => ({ ...p, forecastNumber: 0 }))

  // Compute incompressible needs per packaging type
  const incompressibleNeedsByType = {}
  Object.keys(packagingNeeds).forEach(brand => {
    const brandData = packagingNeeds[brand]
    const isIncompressible = brandData.incompressiblePackagingVolumes

    if (isIncompressible) {
      Object.keys(brandData).forEach(packagingType => {
        if (packagingType !== "incompressiblePackagingVolumes") {
          incompressibleNeedsByType[packagingType] = (incompressibleNeedsByType[packagingType] || 0) + (brandData[packagingType] || 0)
        }
      })
    }
  })

  let remainingForecast = globalPackagingForecastNumber
  const newPackagings = []

  // Step 1: Allocate to incompressible packaging types first
  for (const packaging of packagings) {
    const incompressibleNeed = incompressibleNeedsByType[packaging.type] || 0

    if (incompressibleNeed > 0) {
      // Allocate as much as possible to this incompressible type
      const allocated = Math.min(incompressibleNeed, packaging.theoreticalNumber, remainingForecast)
      newPackagings.push({
        ...packaging,
        forecastNumber: allocated
      })
      remainingForecast -= allocated
    } else {
      // Will allocate in step 2
      newPackagings.push({
        ...packaging,
        forecastNumber: 0
      })
    }
  }

  // Step 2: Distribute remaining forecast proportionally among compressible types
  if (remainingForecast > 0) {
    // Calculate total theoretical for compressible types
    const compressiblePackagings = newPackagings.filter(p => !incompressibleNeedsByType[p.type])
    const totalCompressibleTheoretical = compressiblePackagings.reduce((sum, p) => sum + (p.theoreticalNumber || 0), 0)

    if (totalCompressibleTheoretical > 0) {
      let distributedTotal = 0

      for (let i = 0; i < newPackagings.length; i++) {
        const packaging = newPackagings[i]

        if (!incompressibleNeedsByType[packaging.type]) {
          // Proportional distribution for compressible types
          if (i === newPackagings.length - 1) {
            // Last packaging gets the remainder
            packaging.forecastNumber = remainingForecast - distributedTotal
          } else {
            const proportional = (remainingForecast / totalCompressibleTheoretical) * (packaging.theoreticalNumber || 0)
            packaging.forecastNumber = Math.floor(proportional)
            distributedTotal += packaging.forecastNumber
          }
        }
      }
    }
  }

  return newPackagings
}

export const parseRecipeProductionStepsMigrationCSVFile = (file, callback) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    const csv = e.target.result
    const allTextLines = csv.split(/\r\n|\n/)
    const headers = allTextLines[0].split(/[,;]/)
    const filteredHeaders = headers.filter(header => header.trim() !== "")
    const lines = []

    for (const line of allTextLines.slice(1)) {
      const lineArr = line.split(/[,;]/)

      // force lines to have 5 columns max
      if (lineArr.length > 5) {
        lineArr.splice(5)
      }
      if (filteredHeaders.length > 5) {
        filteredHeaders.splice(5)
      }

      if (lineArr.length === filteredHeaders.length) {
        const columns = []
        for (const [index,] of filteredHeaders.entries()) {
          columns.push(lineArr[index])
        }
        lines.push(columns)
      }
    }
    callback(lines)
  }
  reader.readAsText(file)
}

const getTheLastPackagingsWithStartTime = (status, packagings = []) => {
  // If the status is DONE, we don't care about the previous steps, so no active step
  if (status === "DONE") {
    return null
  }

  const filteredPackagings = packagings
    .filter((packaging) => packaging.startTime && !packaging.endTime)
    .sort((a, b) => dayjs(b.startTime).diff(dayjs(a.startTime)))

  if (filteredPackagings.length > 0) {
    const sortedPackagings = getOrderedPEPackagings(filteredPackagings)
    return sortedPackagings[sortedPackagings.length - 1].type
  }

  return null
}

export const getPECurrentActiveStep = (status, packagings = []) => {
  if (status === PE_STATUSES.waste) return status
  if (status === PE_STATUSES.packagingsCounting) return status
  return getTheLastPackagingsWithStartTime(status, packagings)
}

export const getPEStepperOptions = (packagings = []) => {
  const orderedPackagingByType = [{ type: "BEFORE_START", theoreticalNumber: 1 }, ...getOrderedPEPackagings(packagings), { type: "PACKAGINGS_COUNTING", theoreticalNumber: 1 }]
  let wastedtheoreticalNumber = 0
  const packagingTypeOptions = orderedPackagingByType.reduce((options, packaging) => {
    const currentOption = PE_STEPPER_OPTIONS.find(option => option.value === packaging.type)
    if (currentOption) {
      wastedtheoreticalNumber += packaging.realNumber ? packaging.theoreticalNumber - packaging.realNumber : packaging.theoreticalNumber
      const theoreticalNumber = Math.ceil(packaging.theoreticalNumber) || 0
      if (theoreticalNumber > 0) options.push({ ...currentOption, theoreticalNumber })
    }
    return options
  }, [])

  return [
    ...packagingTypeOptions,
    { ...PE_STEPPER_OPTIONS[PE_STEPPER_OPTIONS.length - 1], theoreticalNumber: wastedtheoreticalNumber },
  ]
}

/**
 * Get the "next" recipe PE to work on from the available list
 *
 * 1. Keep only recipes with status === "TODO".
 * 2. If any have a theoreticalStartTime, pick the one with the earliest time.
 * 3. Otherwise, pick the one with the largest number of sections.
 * 4. If no "TODO" recipes are left, return null.
 */
export const getNextToDoRecipe = (peList = []) => {
  // Keep only TODO recipes
  const todos = peList.filter(pe => pe.status === "TODO")
  if (todos.length === 0) return null

  // Among TODO recipes, keep only those with a valid theoreticalStartTime
  const withTime = todos.filter(pe => Number.isFinite(pe.theoreticalStartTime))

  // Sort ascending by theoreticalStartTime and return the soonest
  if (withTime.length > 0) {
    withTime.sort((a, b) => a.theoreticalStartTime - b.theoreticalStartTime)
    return withTime[0]
  }

  // If none had a theoreticalStartTime, sort by number of sections (descending)
  const getSectionsLen = pe => Array.isArray(pe.sections) ? pe.sections.length : 0
  return todos
    .slice()
    .sort((a, b) => getSectionsLen(b) - getSectionsLen(a))[0]
}

export const getPackagingNeedsByBrand = (productionItems = [], planningRules = {}) => {
  const needs = {}
  productionItems.forEach(item => {
    const brand = item.brand
    const packaging = item.packaging

    if (!brand || !packaging) return
    if (!needs[brand]) needs[brand] = { incompressiblePackagingVolumes: planningRules[brand]?.incompressiblePackagingVolumes || false }
    if (!needs[brand][packaging]) needs[brand][packaging] = 0
    needs[brand][packaging] += item.expectedProduction || 0
  })
  return needs
}

export const checkIsPSEWithMultiPackagingDate = (productionStepExecution) => {
  const packagingExecutions = productionStepExecution.packagingExecutions || []
  const distinctPackagingDates = [...new Set(packagingExecutions.map(pe => pe.packagingDate).filter(Boolean))]
  const isMultiPackagingDate = distinctPackagingDates.length > 1
  return { isMultiPackagingDate, distinctPackagingDates }
}